import { useState, useEffect, useCallback, Suspense } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
  useOutletContext,
} from 'react-router-dom';
import '@/App.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BusinessProvider } from './contexts/BusinessContext';
import { BusinessScopeProvider } from './contexts/BusinessScopeContext';
import { SelectionProvider } from './contexts/SelectionContext';
import { UndoProvider } from './contexts/UndoContext';
import { SavedViewsProvider } from './contexts/SavedViewsContext';
import BusinessScopeBanner from './components/BusinessScopeBanner';
import { ThemeProvider } from './contexts/ThemeContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from 'sonner';

import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import PublicCommitmentView from './pages/PublicCommitmentView';
import OnboardingPage from './pages/OnboardingPage';
import TodayView from './pages/TodayView';

import {
  InboxView, CallsView, ReadingView, PaymentsView, AnalyticsView,
  CalendarView, LayoutPreview, NotesView, SettingsView, ReviewView,
  PolkTaskHub, TemplatesView, DelegatablesView, CommitmentsView, TasksView,
  SettingsHealthView, BriefingPage,
} from './routes/lazyRoutes';

import Sidebar from './components/Sidebar';
import { MobileTabBar } from './components/MobileTabBar';
import Topbar from './components/Topbar';
import RouteProgressBar from './components/RouteProgressBar';
import { AppOverlays } from './components/AppOverlays';
import { SkipToMainLink } from './components/SkipToMainLink';
import { useAppShortcuts } from './hooks/useAppShortcuts';
import { useDaybreakRitual } from './hooks/useDaybreakRitual';
import { commitmentsLoader, analyticsLoader, calendarLoader, inboxLoader, paymentsLoader, todayLoader } from './api/loaders';
import { safeStorage } from './utils/safeStorage';
import { LANDING_THROTTLE_MS, LANDING_TIMESTAMP_KEY } from './config/landingGate';

function RouteFallback() {
  return (
    <div
      className="flex items-center justify-center h-full min-h-[200px]"
      style={{ background: 'var(--pd-bg)' }}
      data-testid="route-fallback"
    >
      <div
        className="flex items-center gap-3 rounded-2xl px-4 py-3"
        style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}
      >
        <div
          className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--pd-border)', borderTopColor: 'transparent' }}
        />
        <span className="text-[12px] font-medium" style={{ color: 'var(--pd-muted)' }}>
          Loading view...
        </span>
      </div>
    </div>
  );
}

const TITLES = {
  '/': 'Today',
  '/tasks': 'Tasks',
  '/inbox': 'Inbox',
  '/calls': 'Calls',
  '/calendar': 'Calendar',
  '/reading': 'Reading',
  '/notes': 'Notes',
  '/payments': 'Payments',
  '/analytics': 'Analytics',
  '/review': 'Review',
  '/hub': 'PolkTaskHub',
  '/templates': 'Templates',
  '/delegatables': 'Delegatables',
  '/commitments': 'Commitments',
  '/settings': 'Settings',
  '/settings/health': 'Code quality',
  '/layouts': 'Studio',
};

/**
 * Shell layout rendered inside the Data Router. Exposes a `setFocusTask`
 * callback via `useOutletContext()` so children (TodayView) can trigger
 * Focus Mode without prop drilling.
 */
function AppShellLayout() {
  const [focusTask, setFocusTask] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [commandBarOpen, setCommandBarOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddBrainDump, setQuickAddBrainDump] = useState(false);
  const location = useLocation();
  const title = TITLES[location.pathname] || 'PolkDesQ';
  const showDate = location.pathname === '/';
  const { daybreakOpen, daybreakPending, openDaybreak, handleDaybreakClose, dismissForToday } = useDaybreakRitual();

  // Restore Cinema-mode preference on first mount
  useEffect(() => {
    if (safeStorage.getString('polkdesq_cinema_mode') === '1') {
      document.documentElement.setAttribute('data-cinema', '');
    }
  }, []);

  useAppShortcuts({
    onToggleNotes: (val) => setNotesOpen((prev) => (val === undefined ? !prev : val)),
    onOpenShortcuts: () => setShortcutsOpen(true),
    onOpenSearch: () => setSearchOpen(true),
    onOpenCommandBar: () => setCommandBarOpen(true),
    onOpenQuickAdd: () => { setQuickAddBrainDump(false); setQuickAddOpen(true); },
    onOpenBrainDump: () => { setQuickAddBrainDump(true); setQuickAddOpen(true); },
  });

  // Stable callback identities so `React.memo`'d `Sidebar` and `Topbar`
  // don't re-render on every AppShellLayout state change (notes
  // open/close, command bar toggle, search open, focus task swap…).
  // Setter functions from `useState` are already referentially stable,
  // so the closures over them don't need to be regenerated.
  const handleMobileClose = useCallback(() => setMobileMenuOpen(false), []);
  const handleOpenNotes = useCallback(() => setNotesOpen(true), []);
  const handleCommandBarOpen = useCallback(() => setCommandBarOpen(true), []);
  const handleSearchOpen = useCallback(() => setSearchOpen(true), []);
  const handleQuickAddOpen = useCallback(() => { setQuickAddBrainDump(false); setQuickAddOpen(true); }, []);
  const handleBrainDumpOpen = useCallback(() => { setQuickAddBrainDump(true); setQuickAddOpen(true); }, []);
  const handleQuickAddClose = useCallback(() => setQuickAddOpen(false), []);
  const handleMobileMenuToggle = useCallback(() => setMobileMenuOpen((prev) => !prev), []);
  const handleOpenMore = useCallback(() => setMobileMenuOpen(true), []);

  const outletContext = { setFocusTask, focusTaskId: focusTask?.id || null, daybreakPending, openDaybreak, dismissDaybreak: dismissForToday };

  return (
    <div className="pd-shell flex min-h-screen" data-testid="app-shell">
      <SkipToMainLink />
      <div className="pd-shell-backdrop" aria-hidden="true" />
      <div className="pd-sidebar-desktop">
        <Sidebar
          mobileOpen={mobileMenuOpen}
          onMobileClose={handleMobileClose}
          onOpenNotes={handleOpenNotes}
        />
      </div>
      <div className="pd-shell-content">
        <Topbar
          title={title}
          showDate={showDate}
          onCommandBarOpen={handleCommandBarOpen}
          onSearchOpen={handleSearchOpen}
          onQuickAddOpen={handleQuickAddOpen}
          onBrainDumpOpen={handleBrainDumpOpen}
          onMobileMenuToggle={handleMobileMenuToggle}
        />
        <RouteProgressBar />
        <BusinessScopeBanner />
        <main className="pd-main-wrap" id="pd-main-content" role="main" tabIndex={-1} style={{ outline: 'none' }}>
          <div className="pd-main-frame pd-route-fade" data-testid="main-frame" key={location.pathname}>
            <ErrorBoundary message="This view encountered an error. Navigate to another page or retry.">
              <Suspense fallback={<RouteFallback />}>
                <Outlet context={outletContext} />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
        <MobileTabBar onOpenMore={handleOpenMore} />
      </div>
      <AppOverlays
        focusTask={focusTask} setFocusTask={setFocusTask}
        notesOpen={notesOpen} setNotesOpen={setNotesOpen}
        commandBarOpen={commandBarOpen} setCommandBarOpen={setCommandBarOpen}
        searchOpen={searchOpen} setSearchOpen={setSearchOpen}
        shortcutsOpen={shortcutsOpen} setShortcutsOpen={setShortcutsOpen}
        daybreakOpen={daybreakOpen} onDaybreakClose={handleDaybreakClose}
        quickAddOpen={quickAddOpen} onQuickAddClose={handleQuickAddClose}
        quickAddBrainDump={quickAddBrainDump}
      />
    </div>
  );
}

function AuthLoading() {
  return (
    <div
      className="flex items-center justify-center h-screen"
      style={{ background: 'var(--pd-bg)' }}
      data-testid="auth-loading"
    >
      <div
        className="flex items-center gap-3 rounded-3xl px-5 py-4"
        style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}
      >
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-bold text-sm"
          style={{ background: 'var(--pd-focus)' }}
        >
          P
        </div>
        <div>
          <div className="text-sm font-semibold" style={{ color: 'var(--pd-text)' }}>
            Loading your desk
          </div>
          <div className="text-xs" style={{ color: 'var(--pd-subtle)' }}>
            Pulling your businesses, tasks, and context together.
          </div>
        </div>
      </div>
    </div>
  );
}

function ProtectedShell() {
  const { user, loading } = useAuth();
  // Capture the welcome-gate decision exactly ONCE per ProtectedShell mount
  // via a lazy useState initializer. We can't use useEffect here because the
  // gate must redirect on the first render, not after a paint. Re-renders
  // within the same mount won't re-fire the initializer, so navigating
  // between protected routes won't loop through /welcome.
  const [welcomeGate] = useState(() => {
    const lastShown = safeStorage.getString(LANDING_TIMESTAMP_KEY);
    const ms = lastShown ? Date.now() - Number(lastShown) : Infinity;
    return Number.isFinite(ms) && ms <= LANDING_THROTTLE_MS ? 'skip' : 'show';
  });
  if (loading) return <AuthLoading />;
  // Logged-out visitors always go to /login. The public marketing
  // landing has been retired — the welcome carousel is now a strictly
  // post-login experience, gated by the 1h throttle below.
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  // Bookmark / saved-login re-entry → show the welcome carousel once per hour
  // per device before letting them into the app shell. /welcome itself
  // stamps the timestamp on mount, so the user won't loop.
  if (welcomeGate === 'show') return <Navigate to="/welcome" replace />;
  return (
    <BusinessProvider>
      <BusinessScopeProvider>
        <SelectionProvider>
          <UndoProvider>
            <SavedViewsProvider>
              <AppShellLayout />
            </SavedViewsProvider>
          </UndoProvider>
        </SelectionProvider>
      </BusinessScopeProvider>
    </BusinessProvider>
  );
}

function OnboardingGate() {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded) return <Navigate to="/" replace />;
  // OnboardingPage calls `useBusiness()` (createBusiness) to seed the user's
  // first workspace, so it needs BusinessProvider in scope. The protected
  // shell wraps it for everything else, but the onboarding route is reached
  // BEFORE the shell mounts, so we wrap here too.
  return (
    <BusinessProvider>
      <OnboardingPage />
    </BusinessProvider>
  );
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.onboarded ? '/' : '/onboarding'} replace />;
  return <LoginPage />;
}

/**
 * WelcomeGate — authed-user landing/welcome carousel.
 *
 * Reached only after ProtectedShell decides the 1h throttle has elapsed
 * (or on a fresh device). Mounting LandingPage in `isAuthed` mode is what
 * stamps the timestamp, so a refresh of /welcome itself is still allowed
 * (the user might want to linger on the carousel a moment longer).
 *
 * If somehow an unauthed user lands here directly, we bounce to /login —
 * the public landing already lives at /.
 */
function WelcomeGate() {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return <LandingPage isAuthed />;
}

/** Root layout: global providers wrap every route's <Outlet />. */
function RootLayout() {
  return (
    <ThemeProvider>
      <PreferencesProvider>
        <AuthProvider>
          <TimezoneProvider>
            <Outlet />
          </TimezoneProvider>
        </AuthProvider>
      </PreferencesProvider>
    </ThemeProvider>
  );
}

// TodayView needs the outlet context to open Focus Mode + the daybreak
// banner controls. A thin wrapper forwards them as explicit props.
function TodayRoute() {
  const ctx = useOutletContext();
  return (
    <TodayView
      onFocusTask={ctx?.setFocusTask}
      focusTaskId={ctx?.focusTaskId}
      daybreakPending={ctx?.daybreakPending}
      onOpenDaybreak={ctx?.openDaybreak}
      onDismissDaybreak={ctx?.dismissDaybreak}
    />
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    hydrateFallbackElement: <AuthLoading />,
    children: [
      { path: '/login', element: <LoginGate /> },
      { path: '/auth/callback', element: <AuthCallback /> },
      { path: '/share/commitment/:token', element: <PublicCommitmentView /> },
      { path: '/onboarding', element: <OnboardingGate /> },
      { path: '/welcome', element: <WelcomeGate /> },
      {
        element: <ProtectedShell />,
        hydrateFallbackElement: <AuthLoading />,
        children: [
          { path: '/', element: <TodayRoute />, loader: todayLoader },
          { path: '/briefing', element: <BriefingPage /> },
          { path: '/tasks', element: <TasksView /> },
          { path: '/inbox', element: <InboxView />, loader: inboxLoader },
          { path: '/calls', element: <CallsView /> },
          { path: '/calendar', element: <CalendarView />, loader: calendarLoader },
          { path: '/reading', element: <ReadingView /> },
          { path: '/notes', element: <NotesView /> },
          { path: '/payments', element: <PaymentsView />, loader: paymentsLoader },
          { path: '/analytics', element: <AnalyticsView />, loader: analyticsLoader },
          { path: '/review', element: <ReviewView /> },
          { path: '/hub', element: <PolkTaskHub /> },
          { path: '/templates', element: <TemplatesView /> },
          { path: '/delegatables', element: <DelegatablesView /> },
          { path: '/commitments', element: <CommitmentsView />, loader: commitmentsLoader },
          { path: '/settings', element: <SettingsView /> },
          { path: '/settings/health', element: <SettingsHealthView /> },
          { path: '/layouts', element: <LayoutPreview /> },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

function App() {
  return (
    <>
      <RouterProvider router={router} />
      <Toaster
        position="bottom-right"
        gap={8}
        offset={{ bottom: 100, right: 24 }}
        mobileOffset={{ bottom: 140, right: 16 }}
        toastOptions={{
          style: {
            background: 'var(--pd-panel)',
            color: 'var(--pd-text)',
            border: '1px solid var(--pd-border)',
            boxShadow: 'var(--pd-shadow-3)',
            fontSize: '13px',
          },
        }}
      />
    </>
  );
}

export default App;
